import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { StoreState } from "../../store/store.interface";
import { CountryMagentoResponse, CountrySelectState } from "./country.interface";
import { PAYMENT_MESSAGE_ID, updateGlobalMessage } from "../alert/global-message";
import { GENERIC_TRANSACTION_ERROR, defaultApiState } from "../../common/constants/api-constants";
import { MessageType } from "../alert/global-message.interface";
import { ActionStatus } from "../../common/constants/action-status";
import environment from "../../common/environment";
import { getResponseData } from "../../common/utils/api-utils";
import { getCustomerToken } from "../customer/customer";
import store from "../../store/store";
import { ShopApi } from "../../common/constants/shop-api";
import { mapCountryMagentoResponse } from "../address/mapping/country-state.mapping";

/*----- DECLARE THUNKS ------*/

export const fetchCountryOptions = createAsyncThunk('country/fetchCountryOptions', async (_data, thunkAPI) => {
  try{
    return await getCountryListMagentoApiCall();
  } catch(error) {
    thunkAPI.dispatch(updateGlobalMessage({
      id: PAYMENT_MESSAGE_ID,
      message: GENERIC_TRANSACTION_ERROR,
      type: MessageType.primary
    }));
    return thunkAPI.rejectWithValue(error);
  }
},{
  condition: ((_arg, {getState} )=>{
    const state = getState() as StoreState;
    if(state.countrySelectState.countryResponse.status === ActionStatus.loading || state.countrySelectState.countryResponse.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});

/*----- CREATE INITIAL STATE ------*/

export const countryStateInitialState: CountrySelectState = {
  countryResponse: defaultApiState,
  selectedCountry: {}
};

/*----- CREATE STORE SLICE ------*/

export const countrySlice = createSlice({
  name:"country",
  initialState: countryStateInitialState,
  reducers:{
    updateCountryStatus(state, action) {
      state.countryResponse.status = action.payload;
    },
    updateSelectedCountry(state, action) {
      state.selectedCountry[action.payload.id]={countryCode: action.payload.country, hasStatesLoaded: false};
    },
    updateHasStatesLoadedForSelectedCountry(state, action) {
      if(state.selectedCountry[action.payload.id]){
        state.selectedCountry[action.payload.id].hasStatesLoaded = action.payload.hasStatesLoaded;
      }
    }
  },
  extraReducers(builder) {
    builder
    .addCase(fetchCountryOptions.pending, (state) => {
      state.countryResponse.status = ActionStatus.loading;
    })
    .addCase(fetchCountryOptions.fulfilled, (state, action) => {
      state.countryResponse.status = ActionStatus.succeeded;
      state.countryResponse.data = action.payload;
    })
    .addCase(fetchCountryOptions.rejected, (state, action) => {
      state.countryResponse.status = ActionStatus.failed;
      state.countryResponse.error = action.payload;
    })
  }
});

/*----- CREATE SLICE ACTIONS ------*/

export const { updateHasStatesLoadedForSelectedCountry, updateSelectedCountry, updateCountryStatus } = countrySlice.actions

/*----- CREATE SELECTORS ------*/

export const getCountryOptions = (state: StoreState) =>
  state.countrySelectState.countryResponse.data;

export const getCountryStatus = (state: StoreState) =>
  state.countrySelectState.countryResponse.status;

export const getCountryError = (state: StoreState) =>
  state.countrySelectState.countryResponse.error;

export const getSelectedCountry = (id: string, state: StoreState) =>
  state.countrySelectState.selectedCountry[id]? state.countrySelectState.selectedCountry[id].countryCode : '';

export const hasStatesLoadedForSelectedCountry = (id: string, state: StoreState) =>
  state.countrySelectState.selectedCountry[id]? state.countrySelectState.selectedCountry[id].hasStatesLoaded : false;

/*----- CREATE REDUCER ------*/

export const countrySelectReducer = countrySlice.reducer;

/*----- API CALLS ------*/

async function getCountryListMagentoApiCall() {
  const token = getCustomerToken(store.getState() as StoreState);
  const url = new URL(`${environment().magentoUrl}${ShopApi.countriesMagento}`);
  const response: CountryMagentoResponse[] = await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
  );
  return mapCountryMagentoResponse(response);
}